import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle, Phone } from 'lucide-react';

// Service images
const serviceImages = {
  1: "https://images.unsplash.com/photo-1626535683369-0edf8fb3bd58?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  2: "https://images.unsplash.com/photo-1756561909277-92753f05f032?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  3: "https://images.unsplash.com/photo-1645754884968-a00b7ed62c43?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  4: "https://images.unsplash.com/photo-1771531072574-af6ed6b954c0?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  5: "https://images.unsplash.com/photo-1768054948628-82319724f0b7?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  6: "https://images.unsplash.com/photo-1748816964372-8a73dbb4bb7a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  7: "https://images.unsplash.com/photo-1649777882133-525e923fd5d7?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  8: "https://images.unsplash.com/photo-1594573677313-757d4aeea449?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  9: "https://images.unsplash.com/photo-1612082452474-10ce7f932d0b?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  10: "https://images.unsplash.com/photo-1732529877647-822cb6f82fce?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  11: "https://images.unsplash.com/photo-1757562593192-e15aa89e7876?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  12: "https://images.unsplash.com/photo-1772300704502-410f0fbd43bb?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  13: "https://images.unsplash.com/photo-1772209415876-76ea6cbc2f0c?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  14: "https://images.unsplash.com/photo-1675860094441-5127d5b05820?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  15: "https://images.unsplash.com/photo-1759330806091-b9a077491cc1?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
};

// Full service data with detailed descriptions
const servicesData = {
  'dry-ice-blasting': {
    id: 1,
    name: 'Dry Ice Blasting',
    slug: 'dry-ice-blasting',
    category: 'Core Service',
    shortDesc: 'CO2 cleaning, waterless, non-abrasive',
    fullDesc: `Dry ice blasting is our signature service and the foundation of everything we do at 23rd Industries. This revolutionary cleaning method uses recycled CO2 pellets accelerated by compressed air to clean surfaces without water, chemicals, or abrasive media.

When the dry ice pellets hit the surface at supersonic speed, they sublimate instantly (turn from solid to gas), creating a thermal shock that lifts contaminants away. The result is a perfectly clean surface with zero secondary waste, zero water damage, and zero downtime for teardown.`,
    benefits: [
      'No water usage - eliminates corrosion risk and drainage concerns',
      'Non-abrasive - safe for delicate surfaces and components',
      'No secondary waste - dry ice sublimates completely',
      'Non-conductive - safe for electrical systems',
      'Environmentally friendly - uses recycled CO2',
      'Reduces downtime - clean equipment in place'
    ],
    applications: [
      'Industrial machinery and equipment',
      'Fleet vehicles and engines',
      'Manufacturing production lines',
      'Food processing equipment',
      'Electrical panels and components',
      'Historical restoration projects'
    ],
    process: [
      'Assessment of cleaning requirements and surface type',
      'Selection of appropriate dry ice pellet size and blast pressure',
      'Preparation and protection of surrounding areas',
      'Systematic cleaning with real-time quality checks',
      'Final inspection and documentation'
    ]
  },
  'heavy-duty-cleaning': {
    id: 2,
    name: 'Heavy Duty Cleaning',
    slug: 'heavy-duty-cleaning',
    category: 'Core Service',
    shortDesc: 'Industrial-grade decontamination',
    fullDesc: `When standard cleaning methods fall short, our heavy-duty cleaning service delivers industrial-grade decontamination for the toughest jobs. We tackle years of buildup, stubborn residues, and contamination that traditional methods simply cannot handle.

Using high-pressure dry ice blasting combined with specialized techniques, we restore equipment and surfaces to like-new condition without damaging underlying materials.`,
    benefits: [
      'Removes years of accumulated buildup',
      'Handles grease, oil, carbon, and industrial residue',
      'Safe for most industrial surfaces',
      'No harsh chemicals required',
      'Significantly faster than manual cleaning',
      'Reaches difficult areas and crevices'
    ],
    applications: [
      'Heavy machinery restoration',
      'Industrial ovens and furnaces',
      'Manufacturing equipment',
      'Foundry and casting equipment',
      'Mining equipment',
      'Oil and gas equipment'
    ],
    process: [
      'Initial contamination assessment',
      'Custom cleaning plan development',
      'Heavy-duty dry ice blasting treatment',
      'Detail cleaning of intricate areas',
      'Quality verification and sign-off'
    ]
  },
  'degreasing': {
    id: 3,
    name: 'Degreasing',
    slug: 'degreasing',
    category: 'Core Service',
    shortDesc: 'Grease, oil, and carbon removal',
    fullDesc: `Our professional degreasing service provides complete removal of grease, oil, and carbon buildup from machinery, equipment, and surfaces. Unlike solvent-based degreasers that leave chemical residues, dry ice blasting removes contaminants completely while being safe for the environment.

This service is essential for maintenance programs, equipment inspections, and restoring operational efficiency to grease-laden machinery.`,
    benefits: [
      'Complete grease and oil removal',
      'No chemical residues left behind',
      'Safe for sensitive components',
      'Improves equipment efficiency',
      'Enables thorough inspections',
      'Extends equipment lifespan'
    ],
    applications: [
      'Engine components and blocks',
      'Hydraulic systems',
      'Gearboxes and transmissions',
      'Industrial kitchen equipment',
      'Printing presses',
      'CNC machines'
    ],
    process: [
      'Grease and oil contamination assessment',
      'Temperature and pressure optimization',
      'Systematic degreasing treatment',
      'Detail work on problem areas',
      'Final cleanliness verification'
    ]
  },
  'oil-stain-removal': {
    id: 4,
    name: 'Oil Stain Removal',
    slug: 'oil-stain-removal',
    category: 'Core Service',
    shortDesc: 'Concrete, floors, equipment surfaces',
    fullDesc: `Stubborn oil stains on concrete, floors, and equipment surfaces are no match for our specialized oil stain removal service. We effectively treat and remove oil contamination from porous and non-porous surfaces without damaging the underlying material.

Our dry ice blasting technique penetrates surface pores to lift oil that has soaked in over time, restoring surfaces to a clean, professional appearance.`,
    benefits: [
      'Removes deep-set oil stains',
      'Safe for concrete and asphalt',
      'No water runoff or drainage issues',
      'Environmentally compliant',
      'Improves facility appearance',
      'Reduces slip hazards'
    ],
    applications: [
      'Warehouse and factory floors',
      'Parking garages',
      'Loading docks',
      'Service bays and workshops',
      'Equipment pads',
      'Driveways and aprons'
    ],
    process: [
      'Stain assessment and surface evaluation',
      'Pre-treatment if necessary',
      'Dry ice blasting treatment',
      'Multiple passes for deep stains',
      'Surface inspection and touch-up'
    ]
  },
  'equipment-restoration': {
    id: 5,
    name: 'Equipment Restoration',
    slug: 'equipment-restoration',
    category: 'Core Service',
    shortDesc: 'Bring machinery back to like-new condition',
    fullDesc: `Our equipment restoration service brings aging machinery back to like-new condition, extending equipment life and improving operational performance. Rather than replacing expensive equipment, restoration offers a cost-effective way to maximize your capital investment.

We remove decades of buildup, corrosion, and contamination to reveal the original surface beneath, making equipment easier to inspect, maintain, and operate.`,
    benefits: [
      'Extends equipment lifespan significantly',
      'Fraction of replacement cost',
      'Improves operational efficiency',
      'Enables accurate condition assessment',
      'Enhances resale value',
      'Reduces maintenance requirements'
    ],
    applications: [
      'Vintage and classic machinery',
      'Construction equipment',
      'Agricultural equipment',
      'Industrial presses and machines',
      'Generators and compressors',
      'Fleet vehicles'
    ],
    process: [
      'Complete equipment assessment',
      'Restoration plan and timeline',
      'Systematic cleaning and restoration',
      'Component-by-component treatment',
      'Final inspection and documentation'
    ]
  },
  'fire-smoke-damage': {
    id: 6,
    name: 'Fire & Smoke Damage',
    slug: 'fire-smoke-damage',
    category: 'Specialized',
    shortDesc: 'Soot, char, and smoke residue removal',
    fullDesc: `Fire and smoke damage requires immediate, professional attention to prevent permanent damage and restore affected areas. Our fire damage cleanup service provides expert removal of soot, char, and smoke residue from structures, equipment, and contents.

Dry ice blasting is particularly effective for fire restoration because it removes smoke odor at the source, cleans without introducing moisture, and can salvage items that other methods cannot.`,
    benefits: [
      'Removes soot and smoke residue completely',
      'Eliminates smoke odor at the source',
      'No water damage risk during cleanup',
      'Salvages equipment and contents',
      'Faster restoration timeline',
      'Reduces total restoration costs'
    ],
    applications: [
      'Structural surfaces and framing',
      'HVAC systems and ductwork',
      'Electrical systems and panels',
      'Machinery and equipment',
      'Historic structures',
      'Vehicle fire damage'
    ],
    process: [
      'Damage assessment and documentation',
      'Salvage evaluation',
      'Systematic soot and char removal',
      'Odor elimination treatment',
      'Final inspection and clearance'
    ]
  },
  'mold-remediation': {
    id: 7,
    name: 'Mold Remediation',
    slug: 'mold-remediation',
    category: 'Specialized',
    shortDesc: 'Non-toxic mold removal',
    fullDesc: `Mold contamination poses serious health risks and structural concerns. Our mold remediation service provides non-toxic mold removal from structures and equipment without introducing additional moisture or harmful chemicals that could make the problem worse.

Dry ice blasting removes mold at the surface level and reaches into porous materials where mold roots, providing thorough remediation that helps prevent regrowth.`,
    benefits: [
      'No water - prevents additional moisture problems',
      'No toxic chemicals required',
      'Reaches mold in porous materials',
      'Safe for occupied buildings',
      'Removes mold without spreading spores',
      'Helps prevent mold regrowth'
    ],
    applications: [
      'Attics and crawl spaces',
      'HVAC systems',
      'Structural framing',
      'Concrete and masonry',
      'Industrial equipment',
      'Storage facilities'
    ],
    process: [
      'Mold assessment and testing',
      'Containment setup',
      'Dry ice mold removal',
      'HEPA air filtration',
      'Clearance testing and verification'
    ]
  },
  'fleet-cleaning': {
    id: 8,
    name: 'Fleet Cleaning',
    slug: 'fleet-cleaning',
    category: 'Specialized',
    shortDesc: 'Engine bays, undercarriages, chassis',
    fullDesc: `Keep your fleet running clean and efficient with our comprehensive fleet cleaning service. We specialize in engine bays, undercarriages, chassis, and frames - areas that collect road grime, oil, and debris that can mask problems and accelerate wear.

Our waterless cleaning is safe for sensitive electrical components, sensors, and wiring harnesses that can be damaged by traditional pressure washing.`,
    benefits: [
      'No water exposure to electrical systems',
      'Safe for sensors and wiring harnesses',
      'Reveals hidden maintenance issues',
      'Improves fleet appearance',
      'Extends component life',
      'DOT inspection ready'
    ],
    applications: [
      'Semi trucks and trailers',
      'Delivery vehicles',
      'Municipal fleets',
      'Construction vehicles',
      'Emergency vehicles',
      'Transit buses'
    ],
    process: [
      'Fleet assessment and scheduling',
      'Engine bay degreasing',
      'Undercarriage and frame cleaning',
      'Wheel well and suspension cleaning',
      'Final inspection and quality check'
    ]
  },
  'paint-stripping': {
    id: 9,
    name: 'Paint Stripping',
    slug: 'paint-stripping',
    category: 'Specialized',
    shortDesc: 'Coating removal without surface damage',
    fullDesc: `Our paint stripping service provides precision coating removal without damaging underlying surfaces. Whether you need to remove old paint for refinishing, strip powder coating, or remove failed coatings, dry ice blasting offers a superior alternative to chemical strippers and abrasive blasting.

The process is gentle enough for delicate substrates yet powerful enough to remove multiple layers of industrial coatings.`,
    benefits: [
      'No surface damage or warping',
      'Removes multiple coating types',
      'No chemical waste disposal',
      'Faster than chemical stripping',
      'Safe for complex geometries',
      'Preserves surface profile'
    ],
    applications: [
      'Automotive restoration',
      'Industrial equipment refinishing',
      'Aerospace components',
      'Marine vessels',
      'Architectural metalwork',
      'Machinery repainting'
    ],
    process: [
      'Coating assessment and testing',
      'Parameter optimization',
      'Systematic coating removal',
      'Surface preparation verification',
      'Ready-for-coating inspection'
    ]
  },
  'rust-removal': {
    id: 10,
    name: 'Rust Removal',
    slug: 'rust-removal',
    category: 'Specialized',
    shortDesc: 'Corrosion treatment and surface prep',
    fullDesc: `Rust and corrosion compromise structural integrity and appearance. Our rust removal service provides effective corrosion treatment and surface preparation for metal components and structures, removing rust while preparing the surface for protective coatings.

Dry ice blasting removes rust without removing base metal, preserving component dimensions and structural integrity.`,
    benefits: [
      'Removes rust without removing base metal',
      'Prepares surface for coating',
      'No abrasive media contamination',
      'Reaches complex geometries',
      'Preserves dimensional tolerances',
      'Environmentally friendly'
    ],
    applications: [
      'Structural steel',
      'Vehicle frames and bodies',
      'Industrial equipment',
      'Piping and tanks',
      'Marine equipment',
      'Historic metalwork'
    ],
    process: [
      'Corrosion assessment',
      'Rust removal treatment',
      'Surface profile verification',
      'Coating preparation',
      'Protection recommendations'
    ]
  },
  'hvac-cleaning': {
    id: 11,
    name: 'HVAC Cleaning',
    slug: 'hvac-cleaning',
    category: 'Facility',
    shortDesc: 'Duct, coil, and ventilation cleaning',
    fullDesc: `Clean HVAC systems operate more efficiently and provide better air quality. Our HVAC cleaning service provides thorough cleaning of ducts, coils, and ventilation components without introducing moisture that can promote mold growth.

Dry ice blasting removes built-up dust, debris, and microbial growth from HVAC systems, improving airflow and indoor air quality while reducing energy costs.`,
    benefits: [
      'Improves indoor air quality',
      'Increases system efficiency',
      'Reduces energy costs',
      'No moisture introduction',
      'Extends equipment life',
      'Removes microbial growth'
    ],
    applications: [
      'Commercial HVAC systems',
      'Industrial ventilation',
      'Clean room systems',
      'Hospital and healthcare facilities',
      'Food processing plants',
      'Data centers'
    ],
    process: [
      'System assessment and access planning',
      'Coil cleaning',
      'Ductwork cleaning',
      'Component cleaning',
      'System testing and verification'
    ]
  },
  'concrete-cleaning': {
    id: 12,
    name: 'Concrete Cleaning',
    slug: 'concrete-cleaning',
    category: 'Facility',
    shortDesc: 'Floors, walls, loading docks',
    fullDesc: `Industrial concrete surfaces accumulate oil, grease, tire marks, and contamination that regular cleaning cannot remove. Our concrete cleaning service provides industrial-grade cleaning for floors, walls, and loading docks without water runoff or chemical concerns.

We restore concrete surfaces to a clean, professional appearance while improving safety by removing slip hazards.`,
    benefits: [
      'Removes deep-set stains',
      'No water runoff issues',
      'Improves facility appearance',
      'Reduces slip hazards',
      'Prepares surfaces for coating',
      'Environmentally compliant'
    ],
    applications: [
      'Warehouse floors',
      'Manufacturing facilities',
      'Loading docks',
      'Parking structures',
      'Food processing plants',
      'Pharmaceutical facilities'
    ],
    process: [
      'Surface assessment',
      'Contamination identification',
      'Systematic cleaning treatment',
      'Detail work on problem areas',
      'Final inspection'
    ]
  },
  'facility-maintenance': {
    id: 13,
    name: 'Facility Maintenance',
    slug: 'facility-maintenance',
    category: 'Facility',
    shortDesc: 'Beams, ceilings, structural cleaning',
    fullDesc: `Industrial facilities accumulate dust, debris, and contamination on beams, ceilings, and structural elements that are difficult to clean with traditional methods. Our facility maintenance service provides comprehensive cleaning of overhead and structural areas.

We help maintain clean, professional facilities while addressing fire code requirements and improving overall workplace conditions.`,
    benefits: [
      'Reaches difficult overhead areas',
      'No scaffolding water damage',
      'Improves facility appearance',
      'Addresses fire code requirements',
      'Reduces airborne contamination',
      'Minimal facility disruption'
    ],
    applications: [
      'Warehouse structures',
      'Manufacturing plants',
      'Distribution centers',
      'Aircraft hangars',
      'Industrial kitchens',
      'Processing facilities'
    ],
    process: [
      'Facility assessment and planning',
      'Access and safety setup',
      'Systematic structural cleaning',
      'Debris collection and removal',
      'Final walkthrough and inspection'
    ]
  },
  'surface-decontamination': {
    id: 14,
    name: 'Surface Decontamination',
    slug: 'surface-decontamination',
    category: 'Facility',
    shortDesc: 'Hazmat, chemical, biological cleanup',
    fullDesc: `When surfaces are contaminated with hazardous materials, chemicals, or biological agents, proper decontamination is critical. Our surface decontamination service provides professional cleanup for controlled environments and sensitive areas.

Dry ice blasting removes contaminants without introducing additional chemicals or moisture, and the sublimation process means no contaminated cleaning media to dispose of.`,
    benefits: [
      'No secondary waste generation',
      'No chemical cross-contamination',
      'Safe for sensitive environments',
      'Thorough decontamination',
      'Reduced disposal costs',
      'Faster return to service'
    ],
    applications: [
      'Pharmaceutical facilities',
      'Chemical processing plants',
      'Laboratories',
      'Nuclear facilities',
      'Biohazard cleanup',
      'Contaminated equipment'
    ],
    process: [
      'Contamination assessment',
      'Decontamination plan development',
      'Containment and safety protocols',
      'Systematic decontamination',
      'Verification and clearance testing'
    ]
  },
  'graffiti-removal': {
    id: 15,
    name: 'Graffiti Removal',
    slug: 'graffiti-removal',
    category: 'Facility',
    shortDesc: 'Exterior surface restoration',
    fullDesc: `Graffiti damages property appearance and can invite additional vandalism if not addressed promptly. Our graffiti removal service provides effective exterior surface restoration without damaging underlying materials or finishes.

Dry ice blasting removes paint and markers from brick, concrete, stone, metal, and other surfaces while preserving the original surface texture and appearance.`,
    benefits: [
      'Preserves original surface',
      'Removes all paint types',
      'Safe for historic buildings',
      'No chemical damage',
      'No water damage',
      'Fast turnaround'
    ],
    applications: [
      'Brick and masonry',
      'Concrete structures',
      'Stone facades',
      'Metal surfaces',
      'Historic buildings',
      'Bridges and infrastructure'
    ],
    process: [
      'Surface and paint assessment',
      'Test area treatment',
      'Full graffiti removal',
      'Surface restoration',
      'Anti-graffiti coating options'
    ]
  }
};

// Get all services as array for navigation
const servicesArray = Object.values(servicesData);

export const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = servicesData[slug];

  if (!service) {
    return (
      <main className="bg-black pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Service Not Found</h1>
          <Link to="/services" className="text-orange-500 hover:underline">← Back to Services</Link>
        </div>
      </main>
    );
  }

  const currentIndex = servicesArray.findIndex(s => s.slug === slug);
  const prevService = currentIndex > 0 ? servicesArray[currentIndex - 1] : null;
  const nextService = currentIndex < servicesArray.length - 1 ? servicesArray[currentIndex + 1] : null;

  return (
    <main className="bg-black pt-20" data-testid="service-detail-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${serviceImages[service.id]})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40" />
        
        <div className="relative z-10 h-full flex flex-col justify-end max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <Link to="/services" className="flex items-center gap-2 text-zinc-400 hover:text-orange-500 mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm uppercase tracking-widest font-mono">Back to Services</span>
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge bg-orange-600 text-black px-4 py-2 mb-4 inline-block">
              {service.category}
            </span>
            <h1 className="text-5xl sm:text-6xl md:text-7xl uppercase tracking-wider font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              {service.name}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h2 className="text-3xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  Overview
                </h2>
                <div className="text-zinc-300 leading-relaxed space-y-4">
                  {service.fullDesc.split('\n\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </motion.div>

              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-12"
              >
                <h2 className="text-3xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  Benefits
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {service.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-black border border-zinc-800">
                      <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Applications */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-12"
              >
                <h2 className="text-3xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  Applications
                </h2>
                <div className="flex flex-wrap gap-3">
                  {service.applications.map((app, i) => (
                    <span key={i} className="bg-zinc-900 border border-zinc-800 text-zinc-300 px-4 py-2 text-sm">
                      {app}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Process */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-12"
              >
                <h2 className="text-3xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  Our Process
                </h2>
                <div className="space-y-4">
                  {service.process.map((step, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 bg-black border border-zinc-800">
                      <span className="w-8 h-8 bg-orange-600 text-black font-bold flex items-center justify-center text-sm flex-shrink-0" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {i + 1}
                      </span>
                      <span className="text-zinc-300">{step}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* CTA Card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-orange-600 p-8"
                >
                  <h3 className="text-2xl uppercase tracking-wide font-bold text-black mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    Get a Free Estimate
                  </h3>
                  <p className="text-black/80 mb-6">
                    Ready to experience professional {service.name.toLowerCase()}? Contact us for a free, no-obligation estimate.
                  </p>
                  <Link 
                    to="/estimate" 
                    className="block bg-black text-white text-center py-3 uppercase tracking-widest font-bold hover:bg-zinc-900 transition-colors"
                    style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                  >
                    Request Estimate
                  </Link>
                  <a 
                    href="tel:8172109693" 
                    className="flex items-center justify-center gap-2 mt-4 text-black font-bold"
                  >
                    <Phone className="w-4 h-4" />
                    (817) 210-9693
                  </a>
                </motion.div>

                {/* Quick Facts */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-zinc-900 border border-zinc-800 p-6"
                >
                  <h3 className="text-xl uppercase tracking-wide font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    Quick Facts
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Service Area</span>
                      <span className="text-white">DFW + 100mi</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Response Time</span>
                      <span className="text-white">Same Day</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Free Demo</span>
                      <span className="text-orange-500">Available</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-8 bg-black border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between">
            {prevService ? (
              <Link 
                to={`/services/${prevService.slug}`}
                className="flex items-center gap-2 text-zinc-400 hover:text-orange-500 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm uppercase tracking-widest font-mono">{prevService.name}</span>
              </Link>
            ) : <div />}
            
            {nextService ? (
              <Link 
                to={`/services/${nextService.slug}`}
                className="flex items-center gap-2 text-zinc-400 hover:text-orange-500 transition-colors"
              >
                <span className="text-sm uppercase tracking-widest font-mono">{nextService.name}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-zinc-950 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl uppercase tracking-wide font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-zinc-400 mb-8">
            Schedule a free on-site demo and see the 23rd Industries difference for yourself.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/estimate" className="btn-primary px-8 py-4 text-lg inline-flex items-center justify-center gap-2">
              Get Free Estimate
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/services" className="btn-outline px-8 py-4 text-lg inline-flex items-center justify-center">
              View All Services
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};
